import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Languages } from 'lucide-react'
import { terminologyApi } from '@/api/terminologyApi'
import { BASE_LOCALE, LOCALE_STORAGE_KEY } from '@/i18n/i18n'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Runtime terminology-profile toggle. Lists locales from the backend, switches the
 * active locale instantly (changeLanguage), and persists the choice to localStorage.
 *
 * With one language this reads as a variant toggle; once multiple languages exist it
 * can split into two selects (language + variant) that compose the code — no other
 * change, because everything keys off the composite locale id.
 */
export function ProfileSwitcher() {
  const { i18n } = useTranslation()
  const { data: profiles, isLoading } = useQuery({
    queryKey: ['terms', 'profiles'],
    queryFn: terminologyApi.listProfiles,
  })

  const current = i18n.resolvedLanguage ?? i18n.language ?? BASE_LOCALE

  async function handleChange(locale: string) {
    await i18n.changeLanguage(locale)
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }

  // Fall back to the active locale so the trigger isn't empty before profiles load.
  const options = profiles ?? [{ id: current, label: current, lang: '', variant: '' }]

  return (
    <div className="flex items-center gap-1.5" title="Terminology profile">
      <Languages className="h-4 w-4 text-muted-foreground" />
      <Select value={current} onValueChange={handleChange} disabled={isLoading}>
        <SelectTrigger className="h-8 w-44 text-xs">
          <SelectValue placeholder="Terminology" />
        </SelectTrigger>
        <SelectContent>
          {options.map((p) => (
            <SelectItem key={p.id} value={p.id} className="text-xs">
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
